import React from 'react';
import { Box, Group, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconAlertTriangle, IconCheck } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { CalendarItem } from '../../hooks/useCalendarApi';
import { sourceDot } from './sourceColor';

type Translate = (key: string, params?: Record<string, unknown>) => string;

/** Месяц приходит числом: название подставляем на языке интерфейса. */
export function itemTitle(t: Translate, item: CalendarItem): string {
  const params: Record<string, unknown> = { ...item.titleParams };
  if (typeof params.month === 'number') {
    params.monthName = dayjs()
      .month(params.month - 1)
      .format('MMMM');
  }
  return t(item.titleKey, params);
}

export const CalendarItemRow: React.FC<{ item: CalendarItem; showPerson: boolean }> = ({
  item,
  showPerson,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const title = itemTitle(t, item);
  // В видах «Команда» и «Организация» человек важнее заголовка: три строки
  // «Квартальный отчёт …» в одной клетке отличаются только именем, а оно
  // дописывалось в конец и обрезалось первым.
  const label = showPerson && item.person ? `${item.person.name} — ${title}` : title;

  const tooltip =
    item.isOverdue && !item.isDone ? `${label} — ${t('calendar.page.overdue')}` : label;

  return (
    <Tooltip label={tooltip} withArrow openDelay={400} multiline maw={320}>
      <UnstyledButton
        className="calendar-item"
        onClick={() => navigate(item.url)}
        aria-label={tooltip}
      >
        <Group gap={6} wrap="nowrap" align="center">
          {/* Точка всегда цветом источника — иначе в виде «Организация», где
              прошлое сплошь просрочено, весь месяц краснеет и легенда начинает
              врать. Просроченность отмечает значок, а не перекраска. */}
          <Box
            aria-hidden
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              flexShrink: 0,
              background: sourceDot(item.source),
              opacity: item.isDone ? 0.4 : 1,
            }}
          />
          {item.isDone && <IconCheck size={11} opacity={0.5} />}
          {item.isOverdue && !item.isDone && (
            <IconAlertTriangle size={11} color="var(--mantine-color-red-6)" />
          )}
          <Text
            size="xs"
            c={item.isDone ? 'dimmed' : undefined}
            td={item.isDone ? 'line-through' : undefined}
            truncate
          >
            {label}
          </Text>
        </Group>
      </UnstyledButton>
    </Tooltip>
  );
};
